// src/routes/certificate-routes.tsx - UPDATED for new structure compatibility
import React, { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
    FaCertificate,
    FaStamp,
    FaGlobe,
    FaFlag,
    FaHandshake,
    FaClipboardCheck,
    FaHistory,
    FaShieldAlt,
    FaLink,
    FaSearch
} from 'react-icons/fa';

// Lazy load certificate components
const VietrroCertificate = lazy(() => import('@/pages/management/certificates/VietrroCertificate'));
const WipoCertificate = lazy(() => import('@/pages/management/certificates/WipoCertificate'));
const NoipCertificate = lazy(() => import('@/pages/management/certificates/NoipCertificate'));
const AssociationLicenses = lazy(() => import('@/pages/management/certificates/AssociationLicenses'));
const LicenseTracking = lazy(() => import('@/pages/management/certificates/LicenseTracking'));
const LicenseApproval = lazy(() => import('@/pages/management/certificates/LicenseApproval'));
const LicenseHistory = lazy(() => import('@/pages/management/certificates/LicenseHistory'));
const CertificateVerification = lazy(() => import('@/pages/management/certificates/CertificateVerification'));

// Integration components
const VietrroIntegration = lazy(() => import('@/pages/management/certificates/integrations/VietrroIntegration'));
const WipoIntegration = lazy(() => import('@/pages/management/certificates/integrations/WipoIntegration'));
const NoipIntegration = lazy(() => import('@/pages/management/certificates/integrations/NoipIntegration'));
const AssociationIntegration = lazy(() => import('@/pages/management/certificates/integrations/AssociationIntegration'));
const VietrroWipoIntegration = lazy(() => import('@/pages/management/certificates/integrations/VietrroWipoIntegration'));

// Certificate routes array - COMPATIBLE with new structure
export const certificateRoutes: RoutesProps[] = [
    // Main Certificate Management Routes - PROTECTED (requires MainLayout)
    {
        path: '/certificates/vietrro',
        name: 'Giấy phép Vietrro',
        element: <VietrroCertificate />,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
        icon: FaStamp,
    },
    {
        path: '/certificates/wipo',
        name: 'Giấy phép WIPO',
        element: <WipoCertificate />,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
        icon: FaGlobe,
    },
    {
        path: '/certificates/noip',
        name: 'Giấy phép NOIP',
        element: <NoipCertificate />,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
        icon: FaFlag,
    },
    {
        path: '/certificates/association',
        name: 'Giấy phép Hiệp hội',
        element: <AssociationLicenses />,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
        icon: FaHandshake,
    },

    // Certificate Process Routes - PROTECTED
    {
        path: '/certificates/tracking',
        name: 'Theo dõi đơn đăng ký',
        element: <LicenseTracking />,
        roles: ['right_owner', 'admin', 'staff', 'legal', 'monitor', 'support_specialist'],
        icon: FaSearch,
    },
    {
        path: '/certificates/approval',
        name: 'Duyệt đơn đăng ký',
        element: <LicenseApproval />,
        roles: ['admin', 'staff', 'legal', 'support_specialist'],
        icon: FaClipboardCheck,
    },
    {
        path: '/certificates/history',
        name: 'Lịch sử giấy phép',
        element: <LicenseHistory />,
        roles: ['right_owner', 'admin', 'staff', 'legal', 'monitor', 'support_specialist'],
        icon: FaHistory,
    },

    // Certificate Verification - PUBLIC (no MainLayout needed, handled separately)
    {
        path: '/certificates/verification',
        name: 'Xác thực giấy phép',
        element: <CertificateVerification />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'guest', 'support_specialist'],
        icon: FaShieldAlt,
    },

    // Integration Routes - PROTECTED (admin only)
    {
        path: '/certificates/integrations/vietrro',
        name: 'Tích hợp VIETRRO',
        element: <VietrroIntegration />,
        roles: ['right_owner', 'admin'],
        icon: FaGlobe,
    },
    {
        path: '/certificates/integrations/wipo',
        name: 'Tích hợp WIPO',
        element: <WipoIntegration />,
        roles: ['right_owner', 'admin'],
        icon: FaGlobe,
    },
    {
        path: '/certificates/integrations/noip',
        name: 'Tích hợp NOIP',
        element: <NoipIntegration />,
        roles: ['right_owner', 'admin'],
        icon: FaFlag,
    },
    {
        path: '/certificates/integrations/association',
        name: 'Tích hợp Hiệp hội',
        element: <AssociationIntegration />,
        roles: ['right_owner', 'admin'],
        icon: FaHandshake,
    },
    {
        path: '/certificates/integrations/vietrro-wipo',
        name: 'Tích hợp Hiệp hội',
        element: <VietrroWipoIntegration />,
        roles: ['right_owner', 'admin'],
        icon: FaHandshake,
    },
];

// Navigation structure - UPDATED for new architecture
export const certificateNavigationStructure = {
    // Certificate Types
    vietrro: {
        key: 'vietrro-certificate',
        translationKey: 'vietrroCertificate',
        path: '/certificates/vietrro',
        icon: FaStamp,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
    },
    wipo: {
        key: 'wipo-certificate',
        translationKey: 'wipoCertificate',
        path: '/certificates/wipo',
        icon: FaGlobe,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
    },
    noip: {
        key: 'noip-certificate',
        translationKey: 'noipCertificate',
        path: '/certificates/noip',
        icon: FaFlag,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
    },
    association: {
        key: 'association-licenses',
        translationKey: 'associationLicenses',
        path: '/certificates/association',
        icon: FaHandshake,
        roles: ['right_owner', 'admin', 'staff', 'legal'],
    },

    // Certificate Processes
    tracking: {
        key: 'license-tracking',
        translationKey: 'licenseTracking',
        path: '/certificates/tracking',
        icon: FaSearch,
        roles: ['right_owner', 'admin', 'staff', 'legal', 'monitor', 'support_specialist'],
    },
    approval: {
        key: 'license-approval',
        translationKey: 'licenseApproval',
        path: '/certificates/approval',
        icon: FaClipboardCheck,
        roles: ['admin', 'staff', 'legal', 'support_specialist'],
    },
    history: {
        key: 'license-history',
        translationKey: 'licenseHistory',
        path: '/certificates/history',
        icon: FaHistory,
        roles: ['right_owner', 'admin', 'staff', 'legal', 'monitor', 'support_specialist'],
    },
    verification: {
        key: 'certificate-verification',
        translationKey: 'certificateVerification',
        path: '/certificates/verification',
        icon: FaShieldAlt,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'guest', 'support_specialist'],
    },

    // Integrations
    integrations: {
        key: 'certificate-integrations',
        translationKey: 'certificateIntegrations',
        icon: FaLink,
        roles: ['right_owner', 'admin'],
        subitems: [
            {
                key: 'vietrro-integration',
                translationKey: 'vietrroIntegration',
                path: '/certificates/integrations/vietrro',
                icon: FaStamp,
                roles: ['right_owner', 'admin'],
            },
            {
                key: 'wipo-integration',
                translationKey: 'wipoIntegration',
                path: '/certificates/integrations/wipo',
                icon: FaGlobe,
                roles: ['right_owner', 'admin'],
            },
            {
                key: 'noip-integration',
                translationKey: 'noipIntegration',
                path: '/certificates/integrations/noip',
                icon: FaFlag,
                roles: ['right_owner', 'admin'],
            },
            {
                key: 'association-integration',
                translationKey: 'associationIntegration',
                path: '/certificates/integrations/association',
                icon: FaHandshake,
                roles: ['right_owner', 'admin'],
            },
            {
                key: 'vietrro-wipo',
                translationKey: 'VietrroWipoIntegration',
                path: '/certificates/integrations/vietrro-wipo',
                icon: FaHandshake,
                roles: ['right_owner', 'admin'],
            },
        ],
    },
};

// Helper functions - COMPATIBLE with new structure
export const getCertificateRoutesForRole = (userRoles: string[]): RoutesProps[] => {
    return certificateRoutes.filter((route) => {
        if (!route.roles || route.roles.length === 0) return true;
        return route.roles.some((role) => userRoles.includes(role));
    });
};

export const getCertificateNavigationForRole = (userRoles: string[]) => {
    const navigation: any[] = [];

    Object.entries(certificateNavigationStructure).forEach(([key, item]) => {
        if (item.roles && item.roles.some((role) => userRoles.includes(role))) {
            if ('subitems' in item && item.subitems) {
                const filteredSubitems = item.subitems.filter((subitem: any) =>
                    subitem.roles.some((role: string) => userRoles.includes(role))
                );

                if (filteredSubitems.length > 0) {
                    navigation.push({
                        ...item,
                        subitems: filteredSubitems,
                    });
                }
            } else {
                navigation.push(item);
            }
        }
    });

    return navigation;
};

// Permission helper functions - UPDATED
export const hasAccessToCertificates = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'admin', 'staff', 'legal', 'monitor', 'support_specialist'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canApproveCertificates = (userRoles: string[]): boolean => {
    const approvalRoles = ['admin', 'staff', 'legal', 'support_specialist'];
    return userRoles.some(role => approvalRoles.includes(role));
};

export const canCreateCertificateApplications = (userRoles: string[]): boolean => {
    const creationRoles = ['right_owner', 'admin'];
    return userRoles.some(role => creationRoles.includes(role));
};

export const canAccessIntegrations = (userRoles: string[]): boolean => {
    const integrationRoles = ['right_owner', 'admin'];
    return userRoles.some(role => integrationRoles.includes(role));
};

// Menu groups - COMPATIBLE with new structure
export const getCertificateMenuGroups = (userRoles: string[]) => {
    const menuGroups: any = {};

    // Primary certificate management
    if (userRoles.some(role => ['right_owner', 'admin', 'staff', 'legal', 'support_specialist'].includes(role))) {
        menuGroups.certificates = {
            key: 'certificates',
            translationKey: 'certificateManagement',
            icon: FaCertificate,
            children: [
                certificateNavigationStructure.vietrro,
                certificateNavigationStructure.wipo,
                certificateNavigationStructure.noip,
                certificateNavigationStructure.association,
                certificateNavigationStructure.tracking,
                certificateNavigationStructure.history,
                certificateNavigationStructure.verification,
            ].filter(item => item.roles.some(role => userRoles.includes(role)))
        };
    }

    // Integration menu
    if (canAccessIntegrations(userRoles)) {
        menuGroups.integrations = certificateNavigationStructure.integrations;
    }

    // Approval menu
    if (canApproveCertificates(userRoles)) {
        menuGroups.approval = {
            key: 'certificate-approval',
            translationKey: 'certificateApproval',
            icon: FaClipboardCheck,
            children: [
                certificateNavigationStructure.approval,
                {
                    ...certificateNavigationStructure.history,
                    key: 'certificate-history-admin',
                    translationKey: 'certificateHistoryAdmin',
                    path: '/certificates/history?view=admin',
                },
                {
                    ...certificateNavigationStructure.verification,
                    key: 'certificate-verification-admin',
                    translationKey: 'certificateVerificationAdmin',
                    path: '/certificates/verification?admin=true',
                }
            ]
        };
    }

    // Public verification
    if (userRoles.includes('guest') || userRoles.length === 0) {
        menuGroups.verification = {
            key: 'certificate-public',
            translationKey: 'certificatePublic',
            icon: FaCertificate,
            children: [certificateNavigationStructure.verification]
        };
    }

    return menuGroups;
};

export default certificateRoutes;